<template>
  <FormQuestion
    @back="emit('back')"
    @click:notes="emit('click:notes')"
    @next="emit('next')"
    :bold="question.is_bold"
    :class="landscape ? 'my-0' : 'mb-6'"
    :color="color"
    :dense="dense"
    :display-notes="displayNotes"
    :elevation="elevation"
    :header="header"
    :hide-actions="hideActions"
    :landscape="landscape"
    :mandatory="question.mandatory"
    :model-value="modelValue"
    :next-disabled="nextDisabled"
    :notes-count="notesCount"
    :outlined="outlined"
    :paddingless="paddingless"
    :processing="processing"
    :published="question.published"
    :reversible="reversible"
    :show-indicators="showIndicators"
    :subtitle="question.subtitle"
    :tile="tile"
    :title="question.title"
    :very-dense="veryDense"
  >
    <div
      v-show="!attachmentUploadDisabled"
      class="px-0 py-6"
      data-testid="attachment-uploader-container"
    >
      <AttachmentUploader
        @uploaded="emit('change:attachments')"
        :ref="['uploader', question.id].join('')"
        :owner="owner"
        data-testid="attachment-uploader"
      />
    </div>

    <div
      v-show="localAttachments.length > 0"
      class="mt-4"
    >
      <AttachmentList
        @change="emit('change:attachments')"
        @delete="emit('change:attachments')"
        :attachments="localAttachments"
        :hide-remove="attachmentDeleteDisabled"
        :processing="processing"
      />
    </div>
  </FormQuestion>
</template>

<script setup>
import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import _ from 'lodash';

const modelValue = defineModel({ type: undefined });

const props = defineProps({
  attachmentGroupId: {
    type: String,
    default: null,
  },
  attachmentOwnerId: {
    type: String,
    default: null,
  },
  attachmentOwnerType: {
    type: String,
    default: null,
  },
  attachmentTagsSupplements: {
    type: Array,
    default: () => [],
  },
  attachmentDeleteDisabled: {
    type: Boolean,
    default: false,
  },
  attachmentUploadDisabled: {
    type: Boolean,
    default: false,
  },
  attachments: {
    type: Array,
    default: null,
  },
  attachmentStatus: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  bold: {
    default: false,
    type: Boolean,
  },
  color: {
    default: 'transparent',
    type: String,
  },
  condensed: {
    type: Boolean,
    default: false,
  },
  dense: {
    default: false,
    type: Boolean,
  },
  displayNotes: {
    type: Boolean,
    default: false,
  },
  divided: {
    type: Boolean,
    default: false,
  },
  veryDense: {
    type: Boolean,
    default: false,
  },
  elevation: {
    default: 0,
    type: Number,
  },
  expanded: {
    default: false,
    type: Boolean,
  },
  header: {
    type: String,
    default: null,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  showIndicators: {
    type: Boolean,
    default: false,
  },
  landscape: {
    type: Boolean,
    default: false,
  },
  notesCount: {
    type: Number,
    default: null,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  paddingless: {
    type: Boolean,
    default: false,
  },
  processing: {
    default: false,
    type: Boolean,
  },
  question: {
    type: Object,
    default: null,
  },
  reversible: {
    type: Boolean,
    default: true,
  },
  schema: {
    type: Object,
    default: null,
  },
  tile: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back', 'change:attachments', 'click:notes', 'next']);

const localAttachments = ref(undefined);

const nextDisabled = computed(() => {
  return props.question.mandatory && localAttachments.value.length === 0;
});

const owner = computed(() => {
  return {
    group_id: props.attachmentGroupId,
    type: props.attachmentOwnerType,
    id: props.attachmentOwnerId,
    tags: [props.question.id, props.attachmentOwnerId].concat(props.attachmentTagsSupplements),
    description: props.question.attachment_description || props.question.title,
  };
});

watch(
  () => props.attachments,
  () => {
    if (props.attachments) {
      if (props.attachmentTagsSupplements.length > 0) {
        const ownerTags = _.sortBy(owner.value.tags);
        localAttachments.value = props.attachments.filter((attachment) => {
          const attachmentTags = _.sortBy(attachment.tags);
          return _.isEqual(attachmentTags, ownerTags);
        });
      } else {
        localAttachments.value = props.attachments.filter((attachment) =>
          attachment.tags.includes(props.question.id),
        );
      }
    } else {
      localAttachments.value = [];
    }
  },
  { immediate: true },
);
</script>
